import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { modalController } from '@ionic/core';
import mime from 'mime-types';

import { CoreoRecord, CoreoRecordAttachment } from '../types';
import { CoreoAPI } from './api.service';
import { readFileAsURL } from './db/filesystem.service';

const getExtension = (mimeType: string) => {
  // mime.extension returns false for 'image/jpg' so we have to do this extra check
  // also make sure we return the 3 character extension to support windows users
  const normalized = mimeType?.toLowerCase() ?? '';
  if (normalized === 'image/jpg' || normalized === 'image/jpeg') {
    return 'jpg';
  }
  return mime.extension(normalized || mimeType);
}

const isHtmlContentType = (contentType: string): boolean => {
  // MIME tokens are case-insensitive (e.g. Text/HTML).
  return contentType.toLowerCase().includes('text/html');
};

const HEIF_FTYP_BRANDS = new Set([
  'heic', 'heif', 'heix', 'hevc', 'hevx', 'mif1', 'msf1', 'avif'
]);

const VIDEO_FTYP_BRANDS = new Set([
  'isom', 'iso2', 'iso3', 'iso4', 'iso5', 'iso6',
  'mp41', 'mp42', 'mp71',
  'avc1', 'av01',
  'qt  ', 'msnv', 'm4v ', 'm4a ', 'm4p ',
  'dash', 'mmp4'
]);

const asciiAt = (bytes: Uint8Array, offset: number, length: number): string => {
  return String.fromCharCode(...bytes.slice(offset, offset + length)).toLowerCase();
};

/** Positive magic-byte check for formats we persist/upload as photos. */
export const bytesLookLikeImage = (bytes: Uint8Array): boolean => {
  // JPEG
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return true;
  }
  // PNG
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
  ) {
    return true;
  }
  // GIF87a / GIF89a
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38
  ) {
    return true;
  }
  // WebP: RIFF....WEBP
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return true;
  }
  // HEIC / HEIF / AVIF: ....ftypXXXX
  if (
    bytes.length >= 12 &&
    asciiAt(bytes, 4, 4) === 'ftyp' &&
    HEIF_FTYP_BRANDS.has(asciiAt(bytes, 8, 4))
  ) {
    return true;
  }
  return false;
};

/** Positive magic-byte check for MP4/MOV (ISO BMFF) and WebM. */
export const bytesLookLikeVideo = (bytes: Uint8Array): boolean => {
  // WebM / Matroska EBML header
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3
  ) {
    return true;
  }
  if (bytes.length < 12 || asciiAt(bytes, 4, 4) !== 'ftyp') {
    return false;
  }
  const brand = asciiAt(bytes, 8, 4);
  if (VIDEO_FTYP_BRANDS.has(brand)) {
    return true;
  }
  // Obscure MP4 brands: accept any ftyp that is not a still-image HEIF/AVIF brand.
  return !HEIF_FTYP_BRANDS.has(brand);
};

const looksLikeHtmlText = (text: string): boolean => {
  const prefix = text.trimStart().slice(0, 64).toLowerCase();
  return (
    prefix.startsWith('<!doctype') ||
    prefix.startsWith('<html') ||
    prefix.startsWith('<head') ||
    prefix.startsWith('<!--') ||
    prefix.includes('<!doctype') ||
    prefix.includes('<html')
  );
};

const base64PrefixToBytes = (data: string): Uint8Array => {
  const raw = data.includes(',') ? data.slice(data.indexOf(',') + 1) : data;
  let chunk = raw.slice(0, 24);
  while (chunk.length % 4 !== 0) {
    chunk += '=';
  }
  try {
    const binary = atob(chunk);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
  } catch {
    return new Uint8Array();
  }
};

const getPreSignedUrl = async (projectId: number, fileName: string, contentType: string, signal: AbortSignal) => {
  const params = new URLSearchParams({ contentType, fileName });
  const response = await CoreoAPI.instance.get(
    `/projects/${projectId}/presigned-url?${params.toString()}`,
    { authentication: true, signal }
  );

  if (!response.ok) {
    throw new Error('Failed to get presigned upload URL');
  }

  const { url } = await response.json() as { url: string };
  return url;
}

const isVideoMimeType = (mimeType?: string): boolean => {
  return (mimeType ?? '').toLowerCase().startsWith('video/');
};

/**
 * Validate attachment body bytes. MIME-aware:
 * - image/* (default): require JPEG/PNG/GIF/WebP/HEIC magic
 * - video/*: require MP4/MOV/WebM magic
 * Always reject empty bodies and HTML SPA shells.
 */
export const assertAttachmentBodyIsImage = async (
  body: Blob | string,
  mimeType: string = 'image/jpeg'
): Promise<void> => {
  const expectVideo = isVideoMimeType(mimeType);
  const notMediaMessage = expectVideo
    ? 'Attachment file is not a video'
    : 'Attachment file is not an image';

  if (typeof body === 'string') {
    // Web Filesystem.readFile may return a base64 string.
    // Capacitor Content-Type headers are not consulted here — body bytes only.
    if (looksLikeHtmlText(body)) {
      throw new Error(`${notMediaMessage} (HTML body)`);
    }
    const bytes = base64PrefixToBytes(body);
    const looksLikeMedia = expectVideo ? bytesLookLikeVideo(bytes) : bytesLookLikeImage(bytes);
    if (!looksLikeMedia) {
      throw new Error(notMediaMessage);
    }
    return;
  }

  if (!body || body.size === 0) {
    throw new Error('Attachment file is empty');
  }

  const bytes = new Uint8Array(await body.slice(0, 16).arrayBuffer());
  const looksLikeMedia = expectVideo ? bytesLookLikeVideo(bytes) : bytesLookLikeImage(bytes);
  if (looksLikeMedia) {
    return;
  }

  const header = new TextDecoder().decode(bytes).trimStart().toLowerCase();
  if (looksLikeHtmlText(header)) {
    throw new Error(`${notMediaMessage} (HTML body)`);
  }
  // Require a known media signature so comment/<head> HTML cannot upload when
  // local Capacitor falsely labels the response as text/html.
  throw new Error(notMediaMessage);
};

const uploadAttachmentData = async (projectId: number, attachment: CoreoRecordAttachment, data: string | Blob, signal: AbortSignal) => {
  await assertAttachmentBodyIsImage(data, attachment.mimeType);

  const extension = getExtension(attachment.mimeType);
  const fileName = new Date().valueOf() + (extension ? `.${extension}` : '');
  const targetUrl = await getPreSignedUrl(projectId, fileName, attachment.mimeType, signal);

  const response = await fetch(targetUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': attachment.mimeType
    },
    body: data,
    signal
  });

  if (!response.ok) {
    throw new Error('Attachment upload failed');
  }

  return targetUrl.split('?')[0];
}

export const uploadAttachment = async (projectId: number, attachment: CoreoRecordAttachment, signal: AbortSignal): Promise<string> => {
  if (!attachment.fileLocation) {
    throw new Error('Attachment has no local file');
  }

  let body: string | Blob;
  if (Capacitor.isNativePlatform()) {
    // readFileAsURL already runs Capacitor.convertFileSrc on native.
    const url = await readFileAsURL(attachment.fileLocation);
    if (!url) {
      throw new Error('Attachment local file could not be read');
    }

    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`Attachment file fetch failed: ${response.status}`);
    }

    // Do not trust response Content-Type here. Capacitor iOS labels
    // extensionless `_capacitor_file_` paths as text/html even when the
    // body is a real JPEG/MP4 (persistProjectFile historically wrote no ext).
    // MIME-aware body magic checks run in uploadAttachmentData.
    body = await response.blob();
  } else {
    const file = await Filesystem.readFile({
      path: attachment.fileLocation,
      directory: Directory.Data
    });
    body = file.data as string | Blob;
  }
  return uploadAttachmentData(projectId, attachment, body, signal);
}

export const uploadAttachmentClone = async (projectId: number, attachment: CoreoRecordAttachment, signal: AbortSignal): Promise<string> => {
  if (!attachment.url?.startsWith('http')) {
    throw new Error('Attachment clone has no remote URL');
  }

  const response = await fetch(attachment.url, { signal });
  if (!response.ok) {
    throw new Error(`Attachment clone fetch failed: ${response.status}`);
  }

  // Remote clone URLs are real HTTP(S) sources — Content-Type is trustworthy.
  // Reject text/html here so HTML that does not begin with <!doctype/<html
  // (e.g. leading comment or <head>) cannot be re-uploaded as an image.
  // Local Capacitor fetches must NOT use this check (see uploadAttachment).
  const contentType = response.headers.get('Content-Type') ?? '';
  if (isHtmlContentType(contentType)) {
    throw new Error('Attachment file is not an image');
  }

  const body = await response.blob();
  return uploadAttachmentData(projectId, attachment, body, signal);
}

export const fetchAttachmentMetadata = async (url: string, signal: AbortSignal): Promise<{ mimeType: string, size: number }> => {
  const req = await fetch(url, {
    method: 'HEAD',
    credentials: 'omit',
    signal
  });
  return {
    mimeType: req.headers.get('Content-Type'),
    size: parseInt(req.headers.get('Content-Length'))
  };
}

export const presentRecordModal = async (id: number, props = {}): Promise<HTMLIonModalElement> => {
  const modal = await modalController.create({
    component: 'app-record',
    componentProps: {
      recordId: id,
      ready: false,
      ...props
    }
  });
  modal.addEventListener('didPresent', () => {
    const record = modal.querySelector('app-record');
    record.ready = true;
  }, { once: true });
  modal.present();
  return modal;
}

export const DB_SAFE_RANGE = 100000;
export const isNewRecord = (record: CoreoRecord): boolean => {
  return record.id < DB_SAFE_RANGE;
}
